import { css } from 'styled-components';

import { CenterFlex, Space } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Typo from '@/components/Typography/Typo';
import { CHART_TYPE } from '@/entities/chart/model/constants';
import {
  ChartCandidateExportButton,
  ChartCandidateSelector,
} from '@/features/(charts)/chart-controller/ui/components';
import ChartCandidateTable from '@/features/(charts)/chart-list/ui/chart-candidate-table';
import ChartChartArea from '@/features/(charts)/chart-renderer/ui/chart-chart-area';
import useColor from '@/hooks/domain/useColor';

import ChartChartSettingBar from '../../chart-chart-setting-bar';

type Props = {
  chartInfo: ChartContextProps;
};

export default function ChartDetailRenderSectionCharts({ chartInfo }: Props) {
  const color = useColor();
  const showCandidateTable = chartInfo.state.type === CHART_TYPE.SINGLE_AXIS;

  return (
    <>
      <Space h={24} />
      <ChartChartSettingBar
        chartInfo={chartInfo}
        isFetched={chartInfo.tableQuery.isFetched}
        dataUpdatedAt={chartInfo.tableQuery.dataUpdatedAt}
        refetch={chartInfo.tableQuery.refetch}
      />
      <Space h={12} />
      <Transition
        css={css`
          display: flex;
          flex: 1;
          flex-direction: column;
        `}
        duration={50}
        status={!chartInfo.tableQuery.isFetching}
        fallback={
          <CenterFlex f={1} style={{ height: '100%' }}>
            <Spinner />
          </CenterFlex>
        }
      >
        {chartInfo.tableQuery.data ? (
          <>
            <ChartChartArea chartInfo={chartInfo} />
            <Space h={20} />
            <ActionBar chartInfo={chartInfo} />

            {showCandidateTable && (
              <>
                <Space h={12} />
                <ChartCandidateTable chartInfo={chartInfo} />
              </>
            )}
          </>
        ) : (
          <CenterFlex f={1} style={{ height: '100%' }}>
            <Typo type="label3" style={{ color: color.gray600 }}>
              Select data sets and apply various filters to create your own
              chart.
            </Typo>
          </CenterFlex>
        )}
      </Transition>
    </>
  );
}

const ActionBar = ({ chartInfo }: { chartInfo: ChartContextProps }) => {
  const isSingleAxis = chartInfo.state.type === CHART_TYPE.SINGLE_AXIS;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {isSingleAxis && (
          <>
            <Title chartInfo={chartInfo} />
            <ChartCandidateSelector chartInfo={chartInfo} />
          </>
        )}
      </div>
      <ChartCandidateExportButton chartInfo={chartInfo} />
    </div>
  );
};

const Title = ({ chartInfo }: Props) => {
  const { state } = chartInfo;

  if (state.type !== CHART_TYPE.SINGLE_AXIS) {
    return null;
  }

  return <Typo type="label2">Selected</Typo>;
};
